import {inject, Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {combineLatest, EMPTY, map, Observable} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";
import firebase from "firebase/compat/app";
import {onValue, ref} from "firebase/database";
import Unsubscribe = firebase.Unsubscribe;
import {AccountModel} from "../models/account.model";
import {AccountsHttpService} from "../services/accounts-http.service";
import {AccountsQueryModel} from "../models/accounts-query.model";
import * as _ from 'lodash';

export interface AccountsComponentState {
  accounts: AccountModel[];
  cursor?: string;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class AccountsComponentStore extends ComponentStore<AccountsComponentState> {

  // Selectors
  readonly accounts$ = this.select((state) => state.accounts);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);

  vm$ = this.select({
    accounts: this.accounts$,
    loading: this.loading$,
    error: this.error$,
    cursor: this.cursor$
  })

  // readonly selectAccountsByIds = (ids$: Observable<string[]>) =>
  //   combineLatest([this.select(state => state.accounts), ids$]).pipe(
  //     map(([accounts, ids]) => accounts.filter(account => ids.includes(account.id)))
  //   );

  private channelsUnsubscribeMap: Map<string, Unsubscribe> = new Map();

  constructor(
    @Inject(AccountsHttpService) private accountHttpService: AccountsHttpService,
    @Inject('firebaseService') private firebaseService
  ) {
    super({
      accounts: [],
      cursor: '',
      loading: false,
      error: null,
    });
  }

  // Updaters
  readonly onSetAccounts = this.updater((state: AccountsComponentState, value: {
    accounts: AccountModel[],
    cursor?: string
  }) => ({
    ...state,
    accounts: _.uniq([...state.accounts, ...value.accounts]),
    cursor: value.cursor,
    loading: false,
    error: null,
  }));

  readonly updateAccount = this.updater((state: AccountsComponentState, updatedAccount: AccountModel) => ({
    ...state,
    accounts: state.accounts.map((account: AccountModel) => {
        return updatedAccount.id === account.id ? {...account, ...updatedAccount} : account;
      }
    ),
  }));

  // Updaters
  readonly onResetAccounts = this.updater((state: AccountsComponentState) => ({
    ...state,
    accounts: [],
    cursor: null,
    loading: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  // Effect for fetching accounts
  readonly getAccounts = this.effect(
    (trigger$: Observable<AccountsQueryModel>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)), // Set loading state
        withLatestFrom(this.cursor$, this.accounts$), // Combine with cursor from the state
        switchMap(([params, cursor, accounts]) => {
            //TODO clean subscribed accounts
            if (params.ids?.length) {

              const accountIds = accounts.map(a => a.id);
              // Find missing IDs (those NOT in the store)
              const idsToRequest = params.ids.filter(id => !accountIds.includes(id));

              if (idsToRequest.length === 0) {
                // All requested IDs exist in the store, SKIP API call
                this.setLoading(false);
                return EMPTY;
              }

              // Update params to fetch only missing IDs
              params = {...params, ids: idsToRequest};
            }
            return this.accountHttpService.queryAccounts({...params, cursor: cursor || ''}).pipe(
              tap({
                next: (response) => {

                  //Subscribe to accounts
                  response.list?.forEach((account: AccountModel) => {
                    this.subscribe(account.id);
                  })
                  return this.onSetAccounts({accounts: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err.message || 'Failed to fetch accounts'),
                finalize: () => this.setLoading(false)
              })
            )
          }
        )
      )
  );

  readonly removeAccounts = this.updater(
    (state, ids: string[]) => ({
      ...state,
      accounts: state.accounts.filter((account) => !ids.includes(account.id))
    })
  );


  // Reset
  readonly resetAccounts = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.unSubscribeAll();
        this.onResetAccounts();
      })
    )
  );

  private unSubscribeAll(): void {
    for (const [id, channelUnsubscribe] of this.channelsUnsubscribeMap) {
      channelUnsubscribe();
    }
    this.channelsUnsubscribeMap.clear();
  }

  // Convert Firebase `onValue` into an observable
  public subscribe(id: string) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;

    const path = `${channel.channelId}/${'accounts_changed'}/${id}`;
    const dbRef = ref(database, path);

    // Listen for real-time updates
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const account: AccountModel = snapshot.val();
        if (!!account) {
          this.updateAccount(account);
        }
      }
    );

    // Cleanup when subscription is closed
    this.channelsUnsubscribeMap.set(id, unsubscribe);
  }

}
